import Icon from "@/components/common/Icon";
import Text from "@/components/common/Text";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CircleX } from "lucide-react";
import React, { useRef } from "react";
import { Button } from "../ui/button";

interface InputFileProps {
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
  selectedFile: File | null;
  disabled: boolean;
  onClearFile: () => void;
}

export function InputFile({
  onFileChange,
  isUploading,
  selectedFile,
  disabled,
  onClearFile,
}: InputFileProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || isUploading) return;
    const file = event.target.files?.[0];
    if (file) {
      onFileChange(event);
    }
  };

  const handleClearFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the file input
    }
    onClearFile();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-2">
        <div
          className={cn(
            "relative flex h-[206px] w-full items-center justify-center rounded-none border border-dashed border-brand-border",
            {
              "cursor-not-allowed opacity-50": disabled || selectedFile,
            }
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            className="hidden"
            disabled={disabled}
          />
          <div className="flex flex-col items-center gap-3 text-center">
            <Icon
              name="uploadProduct"
              size={24}
              className="mx-auto text-brand-muted"
            />
            <div className="flex flex-col gap-2">
              <Text
                variant="normalText"
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
              >
                Supported: CSV · XLS · XLSX
              </Text>
              <Text
                variant="normalText"
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
              >
                Maximum size: 25 MB
              </Text>
            </div>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBrowseClick();
              }}
              className={cn(
                "rounded-none border border-brand-ink px-[26px] py-[8px] font-mono text-[11px] uppercase tracking-[0.18em] text-brand-text transition-colors hover:bg-brand-ink hover:text-white",
                {
                  "pointer-events-none opacity-50": disabled || selectedFile,
                }
              )}
            >
              Upload
            </a>
          </div>
        </div>
      </div>
      {selectedFile && (
        <div className="flex items-center justify-between gap-2 rounded-none border-[0.5px] border-brand-border p-2">
          <span className="text-[16px] text-brand-text">
            {selectedFile.name}
          </span>
          <button
            onClick={handleClearFile}
            className="ml-2 text-brand-muted hover:text-brand-muted"
            title="Clear selected file"
          >
            <CircleX fill="#b5b7ba" color="white" />
          </button>
        </div>
      )}
    </div>
  );
}

type InputUrlProps = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  placeholder?: string;
};

export const InputUrl = ({
  value,
  onChange,
  disabled,
  placeholder,
}: InputUrlProps) => {
  const [url, setUrl] = React.useState<string | null>(null);

  const handleAddUrl = () => {
    if (value.trim()) {
      setUrl(value.trim());
      // Clear the input after adding
      // onChange({
      //   target: { value: "" },
      // } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleRemoveUrl = () => {
    setUrl(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex h-[48px] items-center justify-between gap-4 rounded-none border border-brand-border px-3">
        <Input
          id="url"
          type="url"
          placeholder={placeholder ? placeholder : "Add Website URL"}
          value={value}
          onChange={onChange}
          disabled={disabled || !!url}
          className="w-full border-none"
        />
        <Button
          variant="outline"
          onClick={handleAddUrl}
          disabled={!value.trim() || !!url}
          className="border-brand-border text-brand-muted"
        >
          Add
        </Button>
      </div>

      {url && (
        <div className="flex items-center justify-between gap-2 rounded-none border-[0.5px] border-brand-border p-2">
          <span className="text-[16px] text-brand-text">{url}</span>

          <button
            onClick={handleRemoveUrl}
            className="ml-2 text-brand-muted hover:text-brand-muted"
            title="Remove URL"
          >
            <CircleX fill="#E6E6E6" color="white" />
          </button>
        </div>
      )}
    </div>
  );
};
